import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard,
  FileText,
  FolderOpen,
  Plane,
  Kanban,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Shield,
  Fingerprint,
  Loader2,
  HeartPulse,
  Users,
  Crown,
  ShieldCheck
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { startPasskeyRegistration, isPasskeySupported } from '@/utils/passkey';
import { toast } from 'sonner';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/casa-library', label: 'CASA Library', icon: FileText },
  { path: '/company-docs', label: 'Company Docs', icon: FolderOpen },
  { path: '/operations', label: 'Operations', icon: Plane },
  { path: '/projects', label: 'Projects', icon: Kanban },
  { path: '/medical', label: 'Medical Centre', icon: HeartPulse },
  { path: '/calendar', label: 'Calendar', icon: CalendarDays },
];

const ROLE_COLORS = {
  admin: { bg: 'bg-amber-500/10', text: 'text-amber-400', icon: Crown },
  hofo: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', icon: ShieldCheck },
  pilot: { bg: 'bg-blue-500/10', text: 'text-blue-400', icon: Plane },
  member: { bg: 'bg-slate-500/10', text: 'text-slate-400', icon: Shield }
};

export const Sidebar = ({ onNavigate }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [registeringPasskey, setRegisteringPasskey] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isAdmin = user?.role === 'admin';
  const roleConfig = ROLE_COLORS[user?.role] || ROLE_COLORS.member;
  const RoleIcon = roleConfig.icon;

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleRegisterPasskey = async () => {
    setRegisteringPasskey(true);
    try {
      await startPasskeyRegistration();
      toast.success('Passkey registered! You can now sign in with Face ID / fingerprint.');
    } catch (err) {
      if (err.name !== 'NotAllowedError') {
        toast.error(err.message || 'Failed to register passkey');
      }
    } finally {
      setRegisteringPasskey(false);
    }
  };

  const handleNavClick = () => {
    // Close sidebar on mobile after navigation
    if (onNavigate) onNavigate();
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  return (
    <aside
      data-testid="sidebar"
      className={`fixed left-0 top-0 h-screen bg-[#020617] border-r border-slate-800 flex flex-col z-50 transition-all duration-300 ${
        collapsed ? 'w-[68px]' : 'w-[250px]'
      }`}
    >
      {/* Logo */}
      <div className="p-4 flex items-center gap-3 border-b border-slate-800 min-h-[64px]">
        <img src="https://customer-assets.emergentagent.com/job_aviation-doc-center/artifacts/csy3ijni_4c140981-c1c1-451c-b7c5-b4dcb0e574e7.png" alt="Aviation Compliance" className="h-10 w-10 rounded-md flex-shrink-0 object-cover" />
        {!collapsed && (
          <div className="animate-fade-in overflow-hidden">
            <p className="text-sm font-bold text-slate-100 tracking-tight leading-none">AVIATION</p>
            <p className="text-[10px] text-slate-500 font-mono tracking-wider mt-0.5">COMPLIANCE</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={handleNavClick}
            data-testid={`nav-${item.path.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 group ${
                isActive
                  ? 'bg-slate-800/80 text-[#0ea5e9]'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/40'
              } ${collapsed ? 'justify-center' : ''}`
            }
          >
            <item.icon className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span className="truncate">{item.label}</span>}
          </NavLink>
        ))}
        
        {/* Admin-only: User Management */}
        {isAdmin && (
          <NavLink
            to="/users"
            onClick={handleNavClick}
            data-testid="nav-users"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 group ${
                isActive
                  ? 'bg-amber-500/20 text-amber-400'
                  : 'text-amber-400/70 hover:text-amber-400 hover:bg-amber-500/10'
              } ${collapsed ? 'justify-center' : ''}`
            }
          >
            <Users className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span className="truncate">User Management</span>}
          </NavLink>
        )}
      </nav>

      {/* User section */}
      <div className="border-t border-slate-800 p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-btn"
              className={`flex items-center gap-3 w-full px-2 py-2 rounded-md hover:bg-slate-800/40 transition-colors ${
                collapsed ? 'justify-center' : ''
              }`}
            >
              <div className="relative flex-shrink-0">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-slate-700 text-slate-300 text-xs">{initials}</AvatarFallback>
                </Avatar>
                <div className={`absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full ${roleConfig.bg} flex items-center justify-center border border-slate-900`}>
                  <RoleIcon className={`h-2.5 w-2.5 ${roleConfig.text}`} />
                </div>
              </div>
              {!collapsed && (
                <div className="text-left overflow-hidden">
                  <p className="text-xs font-medium text-slate-200 truncate">{user?.name}</p>
                  <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-56 bg-[#0f172a] border-slate-700">
            <div className="px-2 py-1.5 flex items-center gap-2">
              <Badge className={`${roleConfig.bg} ${roleConfig.text} text-[10px] capitalize`}>
                {user?.role || 'member'}
              </Badge>
              <span className="text-[10px] text-slate-500">
                {user?.role === 'admin' ? 'Full access' : 
                 user?.role === 'hofo' ? 'Head of Flying Ops' :
                 user?.role === 'pilot' ? 'Pilot access' : 'Standard access'}
              </span>
            </div>
            <DropdownMenuSeparator className="bg-slate-700" />
            {isPasskeySupported() && (
              <DropdownMenuItem
                data-testid="register-passkey-btn"
                onClick={handleRegisterPasskey}
                disabled={registeringPasskey}
                className="text-slate-300 focus:bg-slate-800 focus:text-white cursor-pointer"
              >
                {registeringPasskey ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Fingerprint className="h-4 w-4 mr-2 text-emerald-400" />
                )}
                Set Up Passkey / Face ID
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              data-testid="logout-btn"
              onClick={handleLogout}
              className="text-red-400 focus:bg-slate-800 focus:text-red-300 cursor-pointer"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Collapse toggle */}
      <button
        data-testid="sidebar-toggle"
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 h-6 w-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
      >
        {collapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
      </button>
    </aside>
  );
};
